import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, Wand2 } from "lucide-react";
import { useHOH, STEP } from "../../../context/StyleContext";
import StepFrame, { StepTitle } from "../StepFrame";

const VFX = ["Styling Hair", "Dressing Look", "Adding Accessories", "Preparing Occasion", "Creating Final Look"];

export default function FinalLookStep() {
  const { generating, finalLook, runGenerateLook, goTo } = useHOH();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!generating) return;
    setIdx(0);
    const t = setInterval(() => setIdx((p) => (p + 1) % VFX.length), 1500);
    return () => clearInterval(t);
  }, [generating]);

  return (
    <StepFrame className="text-center">
      <StepTitle overline="Step 6" title="Your final look" subtitle="A head-to-heel editorial portrait — same you, fully styled." />

      <AnimatePresence mode="wait">
        {generating ? (
          <motion.div key="gen" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-16 flex flex-col items-center" data-testid="final-generating">
            <div className="w-28 h-28 rounded-full bg-hoh-rose/15 flex items-center justify-center animate-pulse"><Sparkles className="w-12 h-12 text-hoh-rose" /></div>
            <motion.p key={idx} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-8 text-3xl md:text-4xl font-heading font-semibold text-black">{VFX[idx]}…</motion.p>
          </motion.div>
        ) : finalLook ? (
          <motion.div key="look" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="hoh-card p-6 md:p-8 grid md:grid-cols-2 gap-8 items-center text-left" data-testid="final-look-card">
            <div className="rounded-[22px] overflow-hidden shadow-lg aspect-[3/4] mx-auto max-w-sm w-full bg-hoh-cream flex items-center justify-center">
              <img src={finalLook.image} alt="Final look" className="w-full h-full object-contain" data-testid="final-look-image" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <h3 className="text-3xl font-semibold text-black">{finalLook.title}</h3>
                <span className="bg-hoh-navy text-white text-xs px-3 py-1 rounded-full">{finalLook.confidence}%</span>
              </div>
              <dl className="mt-5 space-y-2 text-sm">
                {[["Occasion", finalLook.occasion], ["Hairstyle", finalLook.hairstyle], ["Outfit", finalLook.outfit], ["Accessories", (finalLook.accessories || []).join(", ")], ["Products", (finalLook.products || []).join(", ")]].map(([k, v]) => v ? (
                  <div key={k} className="flex gap-3"><dt className="w-24 shrink-0 text-hoh-rose font-semibold">{k}</dt><dd className="text-hoh-navy">{v}</dd></div>
                ) : null)}
              </dl>
              <button onClick={() => goTo(STEP.BOOK)} className="btn-royal mt-8" data-testid="book-look-cta">Book This Look <ArrowRight className="w-4 h-4" /></button>
            </div>
          </motion.div>
        ) : (
          <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-16">
            <button onClick={runGenerateLook} className="btn-royal" data-testid="regenerate-final-btn"><Wand2 className="w-4 h-4" /> Generate Final Look</button>
          </motion.div>
        )}
      </AnimatePresence>
    </StepFrame>
  );
}
